import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { useQueryClient } from '@tanstack/react-query'
import { FormikButtons } from 'components/Formik/FormikButtons'
import { FormikCheckboxGroup } from 'components/Formik/FormikCheckboxGroup'
import { ResponsivePaper } from 'components/ResponsivePaper'
import { Form, Formik } from 'formik'
import { MyAccountHeader } from 'pages/MyAccount/components/MyAccountHeader'
import { ProfileDetails } from 'pages/MyAccount/components/ProfileDetails'
import { useGetQRCode } from 'pages/MyAccountEdit/hooks/useGetQRCode'
import { usePatchTwoFASetting } from 'pages/MyAccountEdit/hooks/usePatchTwoFASetting'
import { TwoFAVerifyApp } from 'pages/MyAccountEdit/TwoFAVerifyApp'
import { fontFamilies } from 'theme/styles.utils'
import userQueryKeys from 'utils/api/User/userQueryKeys'
import { useIsMobile } from 'utils/hooks/useBreakpoints'
import { useCurrentUser } from 'utils/hooks/useCurrentUser'

import { TwoFAVerifyEmail } from './TwoFAVerifyEmail'
import { TwoFAType, TwoFATypes } from './types'

export const MyAccountTwoFA = () => {
  const [openVerify, setOpenVerify] = useState<TwoFATypes | null>(null)
  const [confirmedTypes, setConfirmedTypes] = useState<TwoFATypes[]>([])
  const [qrCode, setQrCode] = useState<string | null>(null)
  const { mutateAsync: getQRCode, isLoading: isPendingQRCode } = useGetQRCode()
  const { t } = useTranslation()
  const user = useCurrentUser()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { mutate, isLoading } = usePatchTwoFASetting(() => {
    queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
    navigate('/my-account')
  })
  const initialValues: { type: TwoFATypes[] } = {
    type: user?.two_factor_authentication || [TwoFAType.unknown],
  }

  const areArraysEqual = (arr1: number[], arr2: number[]) => {
    if (arr1.length !== arr2.length) return false

    const sorted1 = [...arr1].sort((a, b) => a - b)
    const sorted2 = [...arr2].sort((a, b) => a - b)

    return JSON.stringify(sorted1) === JSON.stringify(sorted2)
  }
  const isTheSame = areArraysEqual(confirmedTypes, user?.two_factor_authentication || [])

  const onSubmit = async (values: typeof initialValues) => {
    if (isTheSame) return

    mutate({ type: confirmedTypes })
  }

  const reset = () => {
    setConfirmedTypes(user?.two_factor_authentication || [])
  }

  useEffect(() => {
    setConfirmedTypes(user?.two_factor_authentication || [])
  }, [user?.two_factor_authentication])

  return (
    <ResponsivePaper>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'auto auto 1fr' }} gap={4}>
            <Box>
              <Grid container spacing={2} direction="column">
                <Grid item>
                  <MyAccountHeader editable />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h3"
                    color="primary"
                    sx={(theme) => ({
                      fontWeight: 900,
                      textAlign: 'center',
                    })}
                  >
                    {user?.profile?.full_name}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Divider orientation={isMobile ? 'horizontal' : 'vertical'} />
            </Box>
            <Box>
              <ProfileDetails user={user} editable />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Collapse in={!openVerify} mountOnEnter unmountOnExit>
              <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmit}>
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body1">{t('profile.two_factor_authentication.description')}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1" mt={2}>
                        {t('profile.two_factor_authentication.description2')}
                      </Typography>
                      <SelectTwoFATypes
                        setConfirmedTypes={setConfirmedTypes}
                        confirmedTypes={confirmedTypes}
                        setOpenVerify={async (value) => {
                          setOpenVerify(value)
                          if (value === TwoFAType.googleAuthenticator) {
                            const responseCode = await getQRCode()
                            setQrCode(responseCode?.data?.qrCode)
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormikButtons
                        isLoading={isLoading}
                        onBack={isTheSame ? undefined : reset}
                        disabled={isTheSame || confirmedTypes.length === 0}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Formik>
            </Collapse>
            <Collapse in={openVerify === TwoFAType.googleAuthenticator} mountOnEnter unmountOnExit>
              <TwoFAVerifyApp
                qrCode={qrCode}
                onBack={() => setOpenVerify(null)}
                onSuccess={() => {
                  setConfirmedTypes((prev) => [
                    ...prev.filter((type) => type !== TwoFAType.googleAuthenticator),
                    TwoFAType.googleAuthenticator,
                  ])
                  setOpenVerify(null)
                }}
              />
            </Collapse>
            <Collapse in={openVerify === TwoFAType.email} mountOnEnter unmountOnExit>
              <TwoFAVerifyEmail
                onBack={() => setOpenVerify(null)}
                onSuccess={() => {
                  setConfirmedTypes((prev) => [...prev.filter((type) => type !== TwoFAType.email), TwoFAType.email])
                  setOpenVerify(null)
                }}
              />
            </Collapse>
            {isPendingQRCode && (
              <Box display="grid" justifyContent="center">
                <CircularProgress size={64} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </ResponsivePaper>
  )
}

type SelectProps = {
  setOpenVerify: (value: TwoFATypes | null) => void
  setConfirmedTypes: Dispatch<SetStateAction<TwoFATypes[]>>
  confirmedTypes?: TwoFATypes[]
}

const SelectTwoFATypes = ({ setConfirmedTypes, confirmedTypes, setOpenVerify }: SelectProps) => {
  return (
    <FormikCheckboxGroup
      name="type"
      label={'profile.two_factor_authentication.choose'}
      controlLabels={[
        {
          label: `profile.two_factor_authentication.${TwoFAType.googleAuthenticator}`,
          description: 'profile.two_factor_authentication.app',
          value: TwoFAType.googleAuthenticator,
        },
        {
          label: `profile.two_factor_authentication.${TwoFAType.email}`,
          value: TwoFAType.email,
          description: 'profile.two_factor_authentication.email',
        },
      ]}
      value={confirmedTypes}
      onChange={(value, checked) => {
        if (checked) {
          setOpenVerify(value)
        } else {
          setOpenVerify(null)
          setConfirmedTypes((prev) => prev.filter((type) => type !== value))
        }
      }}
      controlsProps={{
        color: 'primary',
      }}
      labelProps={{
        sx: {
          fontFamily: fontFamilies.cond,
          color: (theme) => `${theme.palette.text.primary}!important`,
        },
      }}
    />
  )
}
